import type React from 'react';

import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import { Box, Container, Divider, Paper, Stack, Typography } from '@mui/material';

import { themeColors } from '@/config/theme/themeColors.ts';

interface MaintenanceViewProps {
  maintenanceEndDate?: string;
}

export const MaintenanceView: React.FC<MaintenanceViewProps> = ({ maintenanceEndDate }) => {
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        margin: 'auto',
        minHeight: '80dvh',
        p: 2,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={8}
          sx={{
            bgcolor: themeColors.white,
            borderRadius: 3,
            color: themeColors.black,
            overflow: 'hidden',
          }}
        >
          {/* Accent bar */}
          <Box sx={{ bgcolor: themeColors.blue, height: 6 }} />

          <Stack spacing={4} sx={{ alignItems: 'center', p: { sm: 6, xs: 4 } }}>
            {/* Icon badge */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${themeColors.blue}, ${themeColors.darkBlue})`,
                borderRadius: '50%',
                boxShadow: '0 10px 30px rgba(0,111,186,0.35)',
                display: 'grid',
                height: 88,
                placeItems: 'center',
                width: 88,
              }}
            >
              <ConstructionRoundedIcon sx={{ color: themeColors.white, fontSize: 44 }} />
            </Box>

            {/* English */}
            <Stack spacing={1} sx={{ textAlign: 'center' }}>
              <Typography
                component="h2"
                sx={{ color: themeColors.darkBlue, fontWeight: 700 }}
                variant="h5"
              >
                We'll be back soon
              </Typography>
              <Typography sx={{ color: themeColors.darkGrey }} variant="body1">
                Our application is temporarily unavailable due to scheduled maintenance. Please try
                again later. Thank you for your understanding.
              </Typography>
            </Stack>

            <Divider flexItem sx={{ borderColor: themeColors.stroke, width: '100%' }} />

            {/* German */}
            <Stack spacing={1} sx={{ textAlign: 'center' }}>
              <Typography
                component="h2"
                sx={{ color: themeColors.darkBlue, fontWeight: 700 }}
                variant="h5"
              >
                Wartungsarbeiten
              </Typography>
              <Typography sx={{ color: themeColors.darkGrey }} variant="body1">
                Unsere Anwendung ist vorübergehend aufgrund geplanter Wartungsarbeiten nicht
                verfügbar. Bitte versuchen Sie es später erneut. Vielen Dank für Ihr Verständnis.
              </Typography>
            </Stack>

            <Divider flexItem sx={{ borderColor: themeColors.stroke, width: '100%' }} />

            {/* French */}
            <Stack spacing={1} sx={{ textAlign: 'center' }}>
              <Typography
                component="h2"
                sx={{ color: themeColors.darkBlue, fontWeight: 700 }}
                variant="h5"
              >
                Maintenance en cours
              </Typography>
              <Typography sx={{ color: themeColors.darkGrey }} variant="body1">
                Notre application est temporairement indisponible en raison d'une maintenance
                planifiée. Veuillez réessayer plus tard. Merci de votre compréhension.
              </Typography>
            </Stack>

            {maintenanceEndDate && (
              <>
                <Divider flexItem sx={{ borderColor: themeColors.stroke, width: '100%' }} />
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                    bgcolor: themeColors.bg,
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                  }}
                >
                  <AccessTimeRoundedIcon sx={{ color: themeColors.blue, fontSize: 20 }} />
                  <Typography sx={{ color: themeColors.darkGrey }} variant="body2">
                    Expected end / Voraussichtliches Ende / Fin prévue:{' '}
                    <Typography
                      component="span"
                      sx={{ color: themeColors.darkBlue, fontWeight: 700 }}
                      variant="body2"
                    >
                      {maintenanceEndDate}
                    </Typography>
                  </Typography>
                </Stack>
              </>
            )}

            {/* Footer note */}
            <Typography sx={{ color: themeColors.grey, pt: 2 }} variant="caption">
              Orange Cyberdefense
            </Typography>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default MaintenanceView;
